/**
 * @file BoardColumn.tsx
 * @description Колонка доски — одна стадия воронки (phase10.5; phase19.31 —
 * вынесена из `OrdersBoardPage`).
 *
 * Шапка колонки: точка цвета стадии магазина (SC28 — цвет только точкой),
 * название, число карточек и сумма. Колонка — зона сброса перетаскивания;
 * подсветка при наведении — рамкой цвета действия.
 */

import { useDroppable } from "@dnd-kit/core";

import { STAGE_DOT_FALLBACK, type BoardType, type StageRow } from "./board-types";
import { OrderKanbanCard } from "../OrderKanbanCard";
import { ReturnKanbanCard } from "../ReturnKanbanCard";
import { formatMoney, type OrderDto } from "../../../lib/api-orders";
import type { ReturnDto } from "../../../lib/api-returns";
import { safeStageColor } from "../../../lib/stage-color";
import { cn } from "@/lib/utils";

interface Props {
  boardType: BoardType;
  stage: StageRow;
  orders: OrderDto[];
  returns: ReturnDto[];
  onOrderClick: (order: OrderDto) => void;
  onReturnClick: (ret: ReturnDto) => void;
}

/** Сумма колонки: заказы — по итогу, возвраты — по сумме к возврату. */
function columnTotal(boardType: BoardType, orders: OrderDto[], returns: ReturnDto[]): number {
  return boardType === "orders"
    ? orders.reduce((sum, o) => sum + Number(o.totalGross || 0), 0)
    : returns.reduce((sum, r) => sum + Number(r.refundAmount || 0), 0);
}

export function BoardColumn({ boardType, stage, orders, returns, onOrderClick, onReturnClick }: Props) {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage-${stage.id}`,
    data: { type: "stage", stageId: stage.id },
  });
  const count = boardType === "orders" ? orders.length : returns.length;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex h-full min-w-[260px] flex-1 flex-col gap-2 rounded-2xl bg-surface/40 p-3 transition-colors",
        isOver && "bg-primary/5 [box-shadow:inset_0_0_0_2px_var(--primary)]",
      )}
    >
      <div className="flex shrink-0 items-center gap-2 px-1">
        <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: safeStageColor(stage.color) ?? STAGE_DOT_FALLBACK }} />
        <span className="type-body flex-1 truncate font-medium">{stage.name}</span>
        <span className="type-caption tabular-nums text-on-surface-variant">{count}</span>
      </div>
      {count > 0 ? (
        <div className="type-caption shrink-0 px-1 tabular-nums text-on-surface-variant">
          {formatMoney(columnTotal(boardType, orders, returns).toFixed(2))} ₽
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
        {boardType === "orders"
          ? orders.map((order) => <OrderKanbanCard key={order.id} order={order} onClick={() => onOrderClick(order)} />)
          : returns.map((ret) => <ReturnKanbanCard key={ret.id} ret={ret} onClick={() => onReturnClick(ret)} />)}
        {count === 0 && !isOver ? (
          <div className="type-caption py-4 text-center text-on-surface-variant">Перетащите сюда</div>
        ) : null}
      </div>
    </div>
  );
}
